<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  inheritAttrs: false,
});
</script>
<script setup lang="ts">
import { $getBrandingCampaignsWithOrdersSimpleList, $getCampaignSimpleList } from '@/api/campaigns'



interface IProps {
  modelValue?: any
  label?: string
  type?: any
  workOrdersIncluded?: boolean
  isSelectAllVisible?: boolean
  eventGroup?: any
  selectOptions?: any[] | null
}

interface WorkOrder {
  id: number
  name?: string
}

interface Campaign {
  id: number
  name: string
  title?: string
  work_orders?: WorkOrder[]
  selectedWorkOrders?: number[]
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => [],
  label: 'Project',
  type: null,
  workOrdersIncluded: false,
  isSelectAllVisible: false,
  eventGroup: null,
  selectOptions: null,
})

interface CustomEvents {
  (e: 'update:modelValue', newValue: any): void
}

const emit = defineEmits<CustomEvents>()

const attrs = useAttrs()
const isMenuOpen = ref<boolean>(false)
const campaigns = ref<Campaign[]>([])
const campaignsLoading = ref<boolean>(false)
const searchStr = ref<string>('')
const openedNodes = ref<string[]>([])
const selectedSummaryRef = ref<HTMLElement | null>(null)
const selectedChipTextRef = ref<HTMLElement | null>(null)
const isSelectedChipTruncated = ref(false)
let selectedSummaryResizeObserver: ResizeObserver | null = null

const campaignNodeValue = (campaignId: number) => `campaign:${campaignId}`

const campaignTitle = (campaign: Campaign) => campaign.name ?? campaign.title ?? 'Project'

const firstSelectedCampaignTitle = computed(() => {
  const selectedCampaign = props.modelValue?.[0]
  return selectedCampaign?.name ?? selectedCampaign?.title ?? ''
})

const campaignOptions = computed<Campaign[]>(() => {
  if (props.selectOptions)
    return props.selectOptions as Campaign[]

  return campaigns.value
})

const filteredCampaigns = computed<Campaign[]>(() => {
  if (!searchStr.value)
    return campaignOptions.value

  const searchLower = searchStr.value.toLowerCase()

  return campaignOptions.value.filter(campaign => campaignTitle(campaign).toLowerCase().includes(searchLower))
})

const selectedSummary = computed(() => {
  if (!props.modelValue.length)
    return ''

  return ' '
})

const selectedCampaignIds = computed<number[]>(() => {
  return props.modelValue.map((campaign: Campaign) => campaign.id)
})

const selectedWorkOrdersByCampaign = computed<Record<number, number[]>>(() => {
  return props.modelValue.reduce((acc: Record<number, number[]>, campaign: Campaign) => {
    acc[campaign.id] = campaign.selectedWorkOrders ?? []

    return acc
  }, {})
})

const getCampaignWorkOrderIds = (campaign: Campaign) => {
  return (campaign.work_orders ?? []).map(workOrder => workOrder.id)
}

const isCampaignFullySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  if (!allWorkOrderIds.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  return allWorkOrderIds.every(workOrderId => selectedWorkOrders.includes(workOrderId))
}

const isCampaignPartiallySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return false

  const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  if (!allWorkOrderIds.length)
    return false

  const selectedCount = allWorkOrderIds.filter(workOrderId => selectedWorkOrders.includes(workOrderId)).length

  return selectedCount > 0 && selectedCount < allWorkOrderIds.length
}

const isCampaignAnySelected = (campaign: Campaign) => {
  if (!props.workOrdersIncluded || !campaign.work_orders?.length)
    return props.modelValue.some((selected: Campaign) => selected.id === campaign.id)

  const selectedWorkOrders = selectedWorkOrdersByCampaign.value[campaign.id] ?? []

  return selectedWorkOrders.length > 0
}

const allFilteredSelected = computed(() => {
  if (filteredCampaigns.value.length === 0)
    return false

  return filteredCampaigns.value.every(campaign => isCampaignFullySelected(campaign))
})

const someFilteredSelected = computed(() => {
  if (filteredCampaigns.value.length === 0)
    return false

  const hasAnySelected = filteredCampaigns.value.some(campaign => isCampaignAnySelected(campaign))

  return hasAnySelected && !allFilteredSelected.value
})

const isCampaignSelected = (campaignId: number) => {
  return props.modelValue.some((selected: Campaign) => selected.id === campaignId)
}

const isCampaignOpen = (campaignId: number) => {
  return openedNodes.value.includes(campaignNodeValue(campaignId))
}

const isWorkOrderSelected = (campaignId: number, workOrderId: number) => {
  return props.modelValue
    .find((selected: Campaign) => selected.id === campaignId)
    ?.selectedWorkOrders?.includes(workOrderId) ?? false
}

const toggleSelectAll = () => {
  if (allFilteredSelected.value || someFilteredSelected.value) {
    const newValue = props.modelValue.filter((selected: Campaign) =>
      !filteredCampaigns.value.some(campaign => campaign.id === selected.id)
    )

    emit('update:modelValue', newValue)

    return
  }

  const newValue = [...props.modelValue]

  filteredCampaigns.value.forEach(campaign => {
    if (!newValue.some((selected: Campaign) => selected.id === campaign.id)) {
      if (props.workOrdersIncluded && campaign.work_orders?.length) {
        newValue.push({
          ...campaign,
          selectedWorkOrders: getCampaignWorkOrderIds(campaign),
        })
      } else {
        newValue.push(campaign)
      }
    } else if (props.workOrdersIncluded && campaign.work_orders?.length) {
      const campaignIdx = newValue.findIndex((selected: Campaign) => selected.id === campaign.id)

      if (campaignIdx >= 0) {
        newValue[campaignIdx] = {
          ...newValue[campaignIdx],
          selectedWorkOrders: getCampaignWorkOrderIds(campaign),
        }
      }
    }
  })

  emit('update:modelValue', newValue)
}

const toggleCampaignOpen = (campaignId: number) => {
  const nodeValue = campaignNodeValue(campaignId)

  if (openedNodes.value.includes(nodeValue)) {
    openedNodes.value = openedNodes.value.filter(value => value !== nodeValue)

    return
  }

  openedNodes.value = [...openedNodes.value, nodeValue]
}

const toggleCampaignSelection = (campaign: Campaign) => {
  if (props.workOrdersIncluded && campaign.work_orders?.length) {
    const allWorkOrderIds = getCampaignWorkOrderIds(campaign)
    const existingCampaign = props.modelValue.find((selected: Campaign) => selected.id === campaign.id)

    if (isCampaignFullySelected(campaign)) {
      emit('update:modelValue', props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id))

      return
    }

    const nextCampaign = {
      ...(existingCampaign ?? campaign),
      selectedWorkOrders: allWorkOrderIds,
    }

    emit('update:modelValue', [
      ...props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id),
      nextCampaign,
    ])

    return
  }

  if (isCampaignSelected(campaign.id)) {
    emit('update:modelValue', props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id))

    return
  }

  emit('update:modelValue', [...props.modelValue, campaign])
}

const toggleWorkOrderSelection = (campaign: Campaign, workOrderId: number) => {
  const otherCampaigns = props.modelValue.filter((selected: Campaign) => selected.id !== campaign.id)
  const existingCampaign = props.modelValue.find((selected: Campaign) => selected.id === campaign.id)
  const nextCampaign = { ...(existingCampaign ?? campaign) }
  const selectedWorkOrders = new Set(nextCampaign.selectedWorkOrders ?? [])

  if (selectedWorkOrders.has(workOrderId))
    selectedWorkOrders.delete(workOrderId)
  else
    selectedWorkOrders.add(workOrderId)

  if (selectedWorkOrders.size)
    nextCampaign.selectedWorkOrders = [...selectedWorkOrders]
  else
    emit('update:modelValue', otherCampaigns)

  if (selectedWorkOrders.size)
    emit('update:modelValue', [...otherCampaigns, nextCampaign])
}

const loadCampaigns = async (search = searchStr.value) => {
  if (props.selectOptions)
    return

  campaignsLoading.value = true

  const filters = {
    is_select: true,
    type: props.type,
    is_visible: true,
    event_group_or_children: props.eventGroup && props.eventGroup.id ? props.eventGroup.id : undefined,
    search,
    per_page: -1,
  }

  try {
    const apiCall = props.workOrdersIncluded
      ? $getBrandingCampaignsWithOrdersSimpleList
      : $getCampaignSimpleList

    const { data } = await apiCall(filters)
    campaigns.value = data.value || []
  }
  catch (error) {
    console.log(error)
  }
  finally {
    campaignsLoading.value = false
  }
}

const onFocusCampaignSelect = async () => {
  if (!campaigns.value.length && !props.selectOptions)
    await loadCampaigns()
}

const syncOpenedNodes = () => {
  if (!props.workOrdersIncluded)
    return

  const selectedCampaignNodes = props.modelValue
    .filter((campaign: Campaign) => campaign.selectedWorkOrders?.length)
    .map((campaign: Campaign) => campaignNodeValue(campaign.id))

  const searchedCampaignNodes = searchStr.value
    ? filteredCampaigns.value
      .filter(campaign => campaign.work_orders?.length)
      .map(campaign => campaignNodeValue(campaign.id))
    : []

  openedNodes.value = [...new Set([
    ...openedNodes.value,
    ...selectedCampaignNodes,
    ...searchedCampaignNodes,
  ])]
}

const onActivatorFocused = async (isFocused: boolean) => {
  if (!isFocused)
    return

  await onFocusCampaignSelect()
  syncOpenedNodes()
}

const clearAndGetCampaigns = async (value: string) => {
  searchStr.value = value

  if (!props.selectOptions) {
    campaigns.value = []
    await loadCampaigns(value)
  }
}

const handleCampaignSearch = (value: string) => {
  searchStr.value = value

  if (!props.selectOptions)
    handleBackendSearch(value)
}

const handleBackendSearch = useDebounceFn((value: string) => {
  if (!props.selectOptions)
    clearAndGetCampaigns(value)
}, 400)

const clearSelection = () => {
  emit('update:modelValue', [])
}

const updateSelectedChipOverflow = () => {
  const element = selectedChipTextRef.value
  isSelectedChipTruncated.value = !!element && element.scrollWidth > element.clientWidth
}

const onSearchInput = (value: string) => {
  handleCampaignSearch(value)
  syncOpenedNodes()
}

const onToggleWorkOrder = (payload: { campaign: Campaign, workOrderId: number }) => {
  toggleWorkOrderSelection(payload.campaign, payload.workOrderId)
}

watch(isMenuOpen, async isOpen => {
  if (!isOpen)
    return

  await onFocusCampaignSelect()
  syncOpenedNodes()
})

watch(() => props.modelValue, () => {
  syncOpenedNodes()
}, { deep: true, immediate: true })

watch(() => props.type, async () => {
  if (!props.selectOptions)
    await clearAndGetCampaigns('')
})

watch(
  [firstSelectedCampaignTitle, () => props.modelValue.length, isMenuOpen],
  () => {
    nextTick(updateSelectedChipOverflow)
  },
)

watch(
  selectedSummaryRef,
  (element, previousElement) => {
    if (!selectedSummaryResizeObserver) {
      selectedSummaryResizeObserver = new ResizeObserver(() => {
        updateSelectedChipOverflow()
      })
    }

    if (previousElement)
      selectedSummaryResizeObserver.unobserve(previousElement)

    if (element)
      selectedSummaryResizeObserver.observe(element)

    nextTick(updateSelectedChipOverflow)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  selectedSummaryResizeObserver?.disconnect()
  selectedSummaryResizeObserver = null
})

defineExpose({ clearAndGetCampaigns })
</script>

<template>
  <VMenu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    location="bottom"
  >
    <template #activator="{ props: menuActivatorProps }">
      <VTextField
        class="custom-text-field"
        v-bind="{ ...attrs, ...menuActivatorProps }"
        :label="label"
        :model-value="selectedSummary"
        :append-inner-icon="isMenuOpen ? 'tabler-chevron-up' : 'tabler-chevron-down'"
        readonly
        clearable
        @click:clear.stop="clearSelection"
        @update:focused="onActivatorFocused"
      >
        <template
          v-if="props.modelValue && props.modelValue.length"
          #prepend-inner
        >
          <div
            ref="selectedSummaryRef"
            class="multiple-campaigns-select__selection"
          >
            <VChip
              size="small"
              class="multiple-campaigns-select__chip"
            >
              <span
                ref="selectedChipTextRef"
                class="multiple-campaigns-select__chip-text"
              >
                {{ firstSelectedCampaignTitle }}
              </span>
              <VMenu
                v-if="isSelectedChipTruncated"
                activator="parent"
                location="top center"
                open-on-hover
                open-delay="100"
                close-delay="0"
                offset="4"
                class="default-tooltip"
              >
                <span class="px-2">
                  {{ firstSelectedCampaignTitle }}
                </span>
              </VMenu>
            </VChip>
            <span
              v-if="props.modelValue.length > 1"
              class="multiple-campaigns-select__others text-grey text-caption"
            >
              (+{{ props.modelValue.length - 1 }} others)
            </span>
          </div>
        </template>
      </VTextField>
    </template>

    <VCard class="multiple-campaigns-select__menu">
      <VCardText class="pb-2">
        <VTextField
          :model-value="searchStr"
          label="Search projects"
          clearable
          autofocus
          @update:modelValue="onSearchInput"
        />
      </VCardText>

      <VListItem
        v-if="isSelectAllVisible && filteredCampaigns.length > 1"
        class="v-list-item--select-all"
        @click="toggleSelectAll"
      >
        <template #prepend>
          <VCheckboxBtn
            :model-value="allFilteredSelected"
            :indeterminate="someFilteredSelected"
          />
        </template>
        <VListItemTitle>
          {{ allFilteredSelected || someFilteredSelected ? 'Deselect' : 'Select' }} All
          ({{ filteredCampaigns.length }})
        </VListItemTitle>
      </VListItem>

      <VDivider v-if="isSelectAllVisible && filteredCampaigns.length > 1" />

      <div
        v-if="campaignsLoading && !filteredCampaigns.length"
        class="d-flex justify-center align-center multiple-campaigns-select__status"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
        />
      </div>

      <div
        v-else-if="!filteredCampaigns.length"
        class="d-flex justify-center align-center multiple-campaigns-select__status"
      >
        Projects not found
      </div>

      <div
        v-else
        class="multiple-campaigns-select__grid-shell"
      >
        <div class="multiple-campaigns-select__grid">
          <VTooltip
            v-for="campaign in filteredCampaigns"
            :key="campaign.id"
            location="top"
            open-on-hover
            open-delay="100"
            close-delay="0"
            class="default-tooltip"
          >
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                :variant="isCampaignSelected(campaign.id) && 'tonal' "
                :color="isCampaignSelected(campaign.id) ? 'primary' : 'grey-700'"
                class="multiple-campaigns-select__campaign"
                :class="{
                  'multiple-campaigns-select__campaign--selected': isCampaignSelected(campaign.id),
                  'multiple-campaigns-select__campaign--partial': isCampaignPartiallySelected(campaign),
                }"
                @click="toggleCampaignSelection(campaign)"
              >
                <VCheckboxBtn
                  :model-value="isCampaignSelected(campaign.id)"
                  :indeterminate="isCampaignPartiallySelected(campaign)"
                  density="compact"
                  hide-details
                  class="multiple-campaigns-select__campaign-checkbox"
                />
                <span class="multiple-campaigns-select__campaign-text">
                  {{ campaignTitle(campaign) }}
                </span>
              </VBtn>
            </template>

            <span class="px-2">
              {{ campaignTitle(campaign) }}
            </span>
          </VTooltip>
        </div>
      </div>
    </VCard>
  </VMenu>
</template>

<style lang="scss">
.multiple-campaigns-select__selection {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.multiple-campaigns-select__chip {
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;

  .v-chip__content {
    min-width: 0;
    max-width: 100%;
  }
}

.multiple-campaigns-select__chip-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiple-campaigns-select__others {
  flex: 0 0 auto;
  margin-left: 8px;
  white-space: nowrap;
}

.multi-select-v-divider {
  margin: 8px;
  display: block;
}

.multiple-campaigns-select__menu {
  width: min(920px, calc(100vw - 24px));
  min-width: 320px;
}

.multiple-campaigns-select__status {
  min-height: 88px;
  padding: 16px;
}

.multiple-campaigns-select__grid-shell {
  max-height: 360px;
  overflow: auto;
  padding: 0 12px 12px;
}

.multiple-campaigns-select__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 8px;
}

.multiple-campaigns-select__campaign {
  justify-content: flex-start;
  min-height: 52px;
  width: 100%;
  padding: 10px 12px;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;

  .v-btn__content {
    width: 100%;
    min-width: 0;
    justify-content: flex-start;
    gap: 8px;
  }

  &--selected {
    border-color: rgba(var(--v-theme-primary), 0.35);
  }

  &--partial {
    border-style: dashed;
  }
}

.multiple-campaigns-select__campaign-checkbox {
  flex: 0 0 auto;
  pointer-events: none;
}

.multiple-campaigns-select__campaign-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiple-campaigns-select__children {
  padding-left: 30px;
}

.multiple-campaigns-select__child {
  opacity: 0.95;

  &:hover {
    background-color: rgba(0, 0, 0, 0.01);
  }
}

.v-list-item--select-all {
  font-weight: 600;
  background-color: rgba(0, 0, 0, 0.04);

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }
}
</style>
